/* eslint-disable no-restricted-imports */
import { BoardColumns, BoardData, boardEntityName, BoardFile } from '@/backend/features/board-files/shared';
import { handleErrorDialog, showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { MultipartFileUpload, useMultipartUpload } from '@juvare/platform-sdk-react/ui/features/file-multipart-upload';
import { FileUploadProgressRow } from '@juvare/platform-sdk-react/ui/features/file-multipart-upload/components/FileUploadProgressRow';
import { UseMultipartUploadParams } from '@juvare/platform-sdk-react/ui/features/file-multipart-upload/types';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { usePlatformSdkReactTranslation } from '@juvare/platform-sdk-react/ui/hooks/usePlatformSdkReactTranslation';
import { Row } from '@juvare/platform-shared/features/storage';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React from 'react';

export function useHandleUploadFiles(reload: () => void) {
    return React.useCallback(
        (row: Row<BoardColumns>) => {
            return showCustomDialog({
                content: (close) => <Content row={row} close={close} reload={reload} />,
            });
        },
        [reload],
    );
}

const Content = (props: { row: Row<BoardColumns>; close: () => void; reload: () => void }) => {
    const { t } = usePlatformSdkReactTranslation();
    const params = React.useMemo<UseMultipartUploadParams<never>>(
        () => ({
            maximumNumberOfFilesAllowed: 10,
            onBeforeUpload: async (fileUpload) => {
                return {
                    entity: boardEntityName,
                    entityId: props.row.id,
                    attachmentKey: fileUpload.file.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_'),
                };
            },
        }),
        [props.row.id],
    );
    const useMultipartUploadResult = useMultipartUpload(params);
    const { fileUploads, status, submit, reset } = useMultipartUploadResult;

    const [boardFilesFieldUpdated, setBoardFilesFieldUpdated] = React.useState(false);
    const boardStorage = useStorageClient<BoardData, BoardColumns>(boardEntityName);
    React.useEffect(() => {
        if (status === 'completed' && !boardFilesFieldUpdated) {
            const columns = props.row.columns;
            const files: BoardFile[] = [];
            for (let i = 0; i < Math.min(columns.attachmentKeys.length, columns.fileNames.length); i++) {
                files.push({ attachmentKey: columns.attachmentKeys[i], fileName: columns.fileNames[i] });
            }
            for (const fileUpload of fileUploads) {
                if (fileUpload.progress === 100) {
                    const attachmentKey = fileUpload.file.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_');
                    const index = files.findIndex((file) => file.attachmentKey === attachmentKey);
                    files.splice(index === -1 ? files.length : index, 0, { attachmentKey, fileName: fileUpload.file.name });
                }
            }
            boardStorage
                .post({ action: 'partial-update', id: props.row.id, data: { files } })
                .then(() => setBoardFilesFieldUpdated(true))
                .catch(handleErrorDialog);
        }
    }, [props.row, fileUploads, status, boardFilesFieldUpdated, boardStorage]);

    const uploadCompleted =
        fileUploads.every(({ progress, errors }) => progress === 100 || !!errors?.globalError) && boardFilesFieldUpdated;

    return (
        <>
            <DialogTitle variant='h6'>{t('multipartFileUpload.title')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <MultipartFileUpload useMultipartUploadResult={useMultipartUploadResult} />
            </DialogContent>
            <DialogActions>
                <Button
                    color='primary'
                    onClick={() => {
                        props.close();
                        reset();
                    }}
                >
                    {t('multipartFileUpload.cancelButton')}
                </Button>
                <Button color='primary' onClick={submit}>
                    {t('multipartFileUpload.uploadButton')}
                </Button>
            </DialogActions>
            <Dialog open={status !== 'editing'} fullWidth>
                <DialogTitle>{t('multipartFileUpload.uploadProgressTitle')}</DialogTitle>
                <DialogContent>
                    {fileUploads.map(({ file, progress, errors }) => (
                        <FileUploadProgressRow
                            key={file.name}
                            fileName={file.name}
                            fileSize={file.size}
                            progress={progress}
                            error={errors?.globalError}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            props.close();
                            props.reload();
                        }}
                        disabled={!uploadCompleted}
                    >
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
